import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, BookOpen, FileText, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Footer from '../components/Footer';
import EmergencyButton from '../components/EmergencyButton';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

const REFERENCES = [
  {
    category: 'Recovery Programs',
    items: [
      { title: 'Alcoholics Anonymous', url: 'https://www.aa.org', description: 'The fellowship of AA' },
      { title: 'Narcotics Anonymous', url: 'https://www.na.org', description: 'Recovery from drug addiction' },
      { title: 'SMART Recovery', url: 'https://www.smartrecovery.org', description: 'Science-based Recovery' }
    ]
  },
  {
    category: 'Mental Health Resources',
    items: [
      { title: 'Beyond Blue', url: 'https://www.beyondblue.org.au', description: 'Anxiety and depression support' },
      { title: 'Lifeline Australia', url: 'https://www.lifeline.org.au', description: 'Crisis support' },
      { title: 'Black Dog Institute', url: 'https://www.blackdoginstitute.org.au', description: 'Mental health research' }
    ]
  },
  {
    category: 'Educational Resources',
    items: [
      { title: 'NIDA', url: 'https://nida.nih.gov', description: 'National Institute on Drug Abuse' },
      { title: 'SAMHSA', url: 'https://www.samhsa.gov', description: 'Substance Abuse and Mental Health Services' }
    ]
  },
  {
    category: 'Books & Literature',
    items: [
      { title: 'Alcoholics Anonymous (Big Book)', description: 'The foundational text of AA' },
      { title: 'Twelve Steps and Twelve Traditions', description: 'In-depth study of the steps' },
      { title: 'Living Sober', description: 'Practical tips for sobriety' }
    ]
  }
];

function ReferencesPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-blue-200 z-40 h-16 px-4 flex items-center justify-between shadow-sm">
        <button onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="w-5 h-5 text-blue-600" />
        </button>
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
        <EmergencyButton />
      </header>

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            References
          </h1>
          <p className="text-sm text-blue-100 mb-6">
            External resources and recommended reading for your Recovery journey
          </p>

          <div className="space-y-6">
            {REFERENCES.map((category, idx) => (
              <div key={idx} className="bg-white rounded-xl overflow-hidden" data-testid={`reference-category-${idx}`}>
                <div className="bg-blue-50 px-5 py-3 border-b border-blue-100">
                  <h2 className="font-bold text-blue-800 uppercase text-sm tracking-wide flex items-center gap-2">
                    {category.category === 'Books & Literature' ? (
                      <BookOpen className="w-4 h-4" />
                    ) : category.category === 'Educational Resources' ? (
                      <FileText className="w-4 h-4" />
                    ) : (
                      <Globe className="w-4 h-4" />
                    )}
                    {category.category}
                  </h2>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {category.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="p-4 hover:bg-gray-50 transition-colors">
                      {item.url ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between group"
                        >
                          <div>
                            <p className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                              {item.title}
                            </p>
                            <p className="text-sm text-gray-500">{item.description}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                        </a>
                      ) : (
                        <div>
                          <p className="font-medium text-gray-800">{item.title}</p>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 mt-6 border border-white/20">
            <p className="text-sm text-blue-100">
              <strong className="text-white">Note:</strong> External links are provided for informational 
              purposes only. Axiom does not endorse or guarantee the content of external websites. 
              Always consult with your healthcare provider before making changes to your treatment plan.
            </p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default ReferencesPage;
